import {useMemo, useState} from "react";
import {Link} from "react-router-dom";
import {useForm, Controller} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {App as AntdApp, Alert, Popconfirm, Select, Table, Tag, Tooltip} from "antd";
import className from "classnames/bind";
import {Button, FontAwesomeIcon} from "~/components";
import {InputField} from "~/components/Forms";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {
	useAddKeywordItemsMutation,
	useAnalyzeKeywordPageMutation,
	useDeleteKeywordPageTermMutation,
	useGetKeywordPageTermsQuery,
	useKeywordGapMutation,
} from "~/reduxs/api/keywordApiSlice";
import {useGetMySubscriptionQuery} from "~/reduxs/api/subscriptionApiSlice";
import style from "../style/Keyword.module.scss";

const cn = className.bind(style);

const schema = Yup.object().shape({
	url: Yup.string().trim()
		.required('Vui lòng nhập URL cần phân tích')
		.matches(/^https?:\/\//i, 'URL phải bắt đầu bằng http:// hoặc https://'),
});

const KIND_TAG = {
	own:   <Tag color="green">Trang của bạn</Tag>,
	rival: <Tag color="orange">Đối thủ</Tag>,
};

/**
 * Tab "Phân tích trang" (Content gap, gate cờ gói can_keyword_gap):
 *  1. Nhập URL (trang mình hoặc đối thủ — server tự nhận biết theo host dự án; trang ngoài
 *     tính 1 lượt "kiểm tra URL" của gói, cache 24h không tính lại) → trích cụm từ nổi bật.
 *  2. Chọn 2 trang đã phân tích → so sánh: cụm đối thủ có mà mình thiếu/mỏng, kèm số liệu
 *     GSC thật để ưu tiên. Lưu cụm hay vào Kho từ khoá (nguồn `content`).
 */
function KeywordGap({projectId}) {

	const {notification} = AntdApp.useApp();
	const {data: sub} = useGetMySubscriptionQuery();
	const canGap = !!sub?.features?.keyword_gap;

	const {data: pages = [], isFetching: loadingPages} = useGetKeywordPageTermsQuery(projectId, {skip: !canGap});
	const [analyze, {isLoading: analyzing}] = useAnalyzeKeywordPageMutation();
	const [deletePage] = useDeleteKeywordPageTermMutation();
	const [runGap, {isLoading: comparing}] = useKeywordGapMutation();
	const [addItems, {isLoading: saving}] = useAddKeywordItemsMutation();

	const [detail, setDetail] = useState(null);       // trang đang xem cụm từ
	const [baseId, setBaseId] = useState(null);
	const [rivalId, setRivalId] = useState(null);
	const [gapResult, setGapResult] = useState(null);
	const [savedLocal, setSavedLocal] = useState(() => new Set());
	const [savingKey, setSavingKey] = useState(null);
	const [viewingId, setViewingId] = useState(null); // dòng đang mở "Xem"

	const {control, handleSubmit, formState: {errors}} = useForm({
		defaultValues: {url: ''},
		resolver: yupResolver(schema),
	});

	const pageOptions = useMemo(() => pages.map((p) => ({
		value: p.id,
		label: `${p.kind === 'own' ? '[Bạn]' : '[Đối thủ]'} ${p.title || p.url}`,
	})), [pages]);

	if (!canGap) {
		return (
			<div className={cn('ctaPanel')}>
				<FontAwesomeIcon icon="fa-light fa-lock" style={{fontSize: 34, color: '#9ca3af'}} />
				<h3 style={{margin: '14px 0 6px', fontSize: 16, fontWeight: 700}}>Phân tích trang & Content Gap</h3>
				<p style={{color: '#6b7280', maxWidth: 520, margin: '0 auto 18px'}}>
					Trích cụm từ nổi bật của trang bạn và trang đối thủ, chỉ ra chủ đề đối thủ phủ mà bạn còn thiếu.
					Tính năng thuộc gói Chuyên nghiệp trở lên.
				</p>
				<Link to="/subscription">
					<Button primary leftIcon={<FontAwesomeIcon icon="fa-light fa-rocket" />}>Xem gói & nâng cấp</Button>
				</Link>
			</div>
		);
	}

	const onAnalyze = async ({url}) => {
		try {
			const res = await analyze({projectId, url: url.trim()}).unwrap();
			setDetail(res?.row || null);
			if (res?.cached) {
				notification.info({message: 'Dùng bản phân tích gần nhất', description: 'Trang này đã phân tích trong 24h — không tính thêm lượt kiểm tra.'});
			} else {
				notification.success({message: 'Đã phân tích trang', description: res?.row?.url});
			}
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không phân tích được trang')});
		}
	};

	const onView = async (row) => {
		try {
			setViewingId(row.id);
			const res = await analyze({projectId, url: row.url}).unwrap();
			setDetail(res?.row || null);
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không tải được cụm từ của trang')});
		} finally {
			setViewingId(null);
		}
	};

	const onDelete = async (row) => {
		try {
			await deletePage({projectId, id: row.id}).unwrap();
			if (detail?.id === row.id) setDetail(null);
			notification.success({message: 'Đã xoá', description: row.url});
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không xoá được')});
		}
	};

	const onCompare = async () => {
		if (!baseId || !rivalId || baseId === rivalId) {
			notification.warning({message: 'Chọn 2 trang khác nhau', description: 'Chọn trang gốc (của bạn) và trang so sánh (đối thủ).'});
			return;
		}
		try {
			const res = await runGap({projectId, base_id: baseId, rival_id: rivalId}).unwrap();
			setGapResult(res);
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không so sánh được')});
		}
	};

	const isSaved = (t) => t.saved || savedLocal.has(t.term);

	const saveTerm = async (t, sourceUrl, key) => {
		try {
			setSavingKey(key);
			const res = await addItems({
				projectId,
				items: [{
					keyword: t.term,
					source: 'content',
					...(t.gsc ? {impressions: t.gsc.impressions, position: t.gsc.position} : {}),
					meta: {url: sourceUrl},
				}],
			}).unwrap();
			setSavedLocal((prev) => new Set(prev).add(t.term));
			if (res?.added > 0) {
				notification.success({message: 'Đã lưu vào kho', description: t.term});
			} else {
				notification.info({message: 'Đã có', description: 'Cụm từ này đã có trong kho.'});
			}
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không lưu được')});
		} finally {
			setSavingKey(null);
		}
	};

	const termCols = (sourceUrl, keyPrefix, extra = []) => [
		{
			title: 'Cụm từ', dataIndex: 'term', key: 'term', ellipsis: true,
			render: (v, t) => (
				<span>
					{v}{' '}
					{isSaved(t) && <FontAwesomeIcon icon="fa-solid fa-circle-check" style={{color: '#16a34a'}} />}
				</span>
			),
		},
		// Cụm/Density là cột phụ — ẩn dưới 768px cho gọn mobile.
		{title: 'Cụm', dataIndex: 'ngram', key: 'ngram', width: 80, align: 'center', responsive: ['md'], render: (v) => <Tag>{v} từ</Tag>},
		{title: 'Lần', dataIndex: 'count', key: 'count', width: 80, align: 'center', sorter: (a, b) => a.count - b.count, defaultSortOrder: 'descend'},
		{title: 'Density', dataIndex: 'density', key: 'density', width: 90, align: 'center', responsive: ['md'], render: (v) => `${v}%`},
		...extra,
		{
			title: '', key: 'action', align: 'right', width: 60,
			render: (_, t) => (
				<Tooltip title={isSaved(t) ? 'Đã có trong kho' : 'Lưu vào kho từ khoá'}>
					<Button small white disabled={isSaved(t) || saving} loading={savingKey === `${keyPrefix}-${t.term}`}
						onClick={() => saveTerm(t, sourceUrl, `${keyPrefix}-${t.term}`)}
						leftIcon={<FontAwesomeIcon icon="fa-light fa-bookmark" />} />
				</Tooltip>
			),
		},
	];

	const gscCol = {
		title: 'GSC', key: 'gsc', width: 170, align: 'center',
		render: (_, t) => t.gsc ? (
			<Tooltip title={`${t.gsc.clicks} clicks (${t.gsc.from} → ${t.gsc.to})`}>
				<Tag color="green">{t.gsc.impressions.toLocaleString('vi-VN')} impr · vị trí {Number(t.gsc.position).toFixed(1)}</Tag>
			</Tooltip>
		) : <span className={cn('hint')}>—</span>,
	};

	const pageCols = [
		{
			title: 'Trang', dataIndex: 'url', key: 'url', ellipsis: true,
			render: (v, r) => (
				<span>
					{KIND_TAG[r.kind] || null}{' '}
					<a href={v} target="_blank" rel="noreferrer">{r.title || v}</a>
				</span>
			),
		},
		// Cột phụ — ẩn dưới 768px cho gọn mobile.
		{title: 'Số từ', dataIndex: 'word_count', key: 'wc', width: 90, align: 'center', responsive: ['md'], render: (v) => v.toLocaleString('vi-VN')},
		{title: 'Cụm từ', dataIndex: 'terms_count', key: 'tc', width: 90, align: 'center', responsive: ['md']},
		{title: 'Phân tích lúc', dataIndex: 'fetched_at', key: 'at', width: 150, responsive: ['md'], render: (v) => <span className={cn('hint')}>{v || '—'}</span>},
		{
			title: '', key: 'action', align: 'right', width: 110,
			render: (_, r) => (
				<span style={{display: 'inline-flex', gap: 6}}>
					<Tooltip title="Xem cụm từ nổi bật">
						<Button small white loading={viewingId === r.id} disabled={analyzing && viewingId !== r.id}
							onClick={() => onView(r)} leftIcon={<FontAwesomeIcon icon="fa-light fa-eye" />} />
					</Tooltip>
					<Popconfirm title="Xoá trang này khỏi danh sách phân tích?" okText="Xoá" cancelText="Huỷ" onConfirm={() => onDelete(r)}>
						<Button small red leftIcon={<FontAwesomeIcon icon="fa-light fa-trash" />} />
					</Popconfirm>
				</span>
			),
		},
	];

	return (
		<div>
			<Alert type="info" showIcon style={{marginBottom: 14}}
				message="Phân tích URL của bạn hoặc đối thủ để trích cụm từ nổi bật (kết quả cache 24h). URL NGOÀI dự án tính 1 lượt 'kiểm tra URL' của gói." />

			{/* Scope class toàn cục `form` để InputField (.form-group/.form-control) ăn style chuẩn của site. */}
			<div className={cn('section') + ' form'}>
				<h3 className={cn('sectionTitle')}>Phân tích một trang</h3>
				<Controller control={control} name="url" render={({field}) => (
					<InputField label="URL trang (của bạn hoặc đối thủ)" placeholder="https://doi-thu.vn/dich-vu-seo" errors={errors} {...field} />
				)} />
				<Button primary loading={analyzing && !viewingId} onClick={handleSubmit(onAnalyze)}
					leftIcon={<FontAwesomeIcon icon="fa-light fa-magnifying-glass" />}>
					Phân tích
				</Button>
			</div>

			{pages.length > 0 && (
				<div className={cn('section')}>
					<h3 className={cn('sectionTitle')}>Trang đã phân tích ({pages.length})</h3>
					<Table rowKey="id" size="small" loading={loadingPages}
						columns={pageCols} dataSource={pages}
						scroll={{x: 'max-content'}}
						pagination={{pageSize: 10, hideOnSinglePage: true, showSizeChanger: false}} />

					<div className={cn('filterBar') + ' form'} style={{marginTop: 14, marginBottom: 0}}>
						<Select style={{minWidth: 260, maxWidth: 380}} placeholder="Trang gốc (thường là trang của bạn)"
							options={pageOptions} value={baseId} onChange={setBaseId} />
						<FontAwesomeIcon icon="fa-light fa-arrow-right-arrow-left" style={{color: '#9ca3af'}} />
						<Select style={{minWidth: 260, maxWidth: 380}} placeholder="Trang so sánh (đối thủ)"
							options={pageOptions} value={rivalId} onChange={setRivalId} />
						<Button primary small loading={comparing} onClick={onCompare}
							leftIcon={<FontAwesomeIcon icon="fa-light fa-code-compare" />}>So sánh Content Gap</Button>
					</div>
				</div>
			)}

			{detail && (
				<div className={cn('section')}>
					<h3 className={cn('sectionTitle')}>
						Cụm từ nổi bật — {KIND_TAG[detail.kind] || null} <a href={detail.url} target="_blank" rel="noreferrer">{detail.title || detail.url}</a>
					</h3>
					<p className={cn('hint')} style={{margin: '0 0 12px'}}>
						{detail.word_count.toLocaleString('vi-VN')} từ · density là chỉ số TƯƠNG ĐỐI để so giữa các trang (title/heading được nhân trọng số).
					</p>
					<Table rowKey="term" size="small"
						columns={termCols(detail.url, 'detail')}
						dataSource={detail.terms}
						scroll={{x: 'max-content'}}
						pagination={{pageSize: 15, hideOnSinglePage: true, showSizeChanger: false}} />
				</div>
			)}

			{gapResult && (
				<div className={cn('section')}>
					<h3 className={cn('sectionTitle')}>
						<FontAwesomeIcon icon="fa-light fa-code-compare" /> Content Gap:
						{' '}<a href={gapResult.rival.url} target="_blank" rel="noreferrer">{gapResult.rival.title || gapResult.rival.url}</a>
						{' '}so với{' '}
						<a href={gapResult.base.url} target="_blank" rel="noreferrer">{gapResult.base.title || gapResult.base.url}</a>
					</h3>
					<p className={cn('hint')} style={{margin: '0 0 12px'}}>
						{gapResult.common_count} cụm trùng nhau · {gapResult.missing.length} cụm đối thủ có mà trang bạn KHÔNG có · {gapResult.weaker.length} cụm bạn phủ mỏng hơn hẳn.
					</p>

					<h4 style={{fontSize: 14, fontWeight: 600, margin: '0 0 8px'}}>Đối thủ có — bạn chưa có ({gapResult.missing.length})</h4>
					<Table rowKey="term" size="small"
						columns={termCols(gapResult.rival.url, 'missing', [gscCol])}
						dataSource={gapResult.missing}
						scroll={{x: 'max-content'}}
						pagination={{pageSize: 15, hideOnSinglePage: true, showSizeChanger: false}} />

					{gapResult.weaker.length > 0 && (
						<>
							<h4 style={{fontSize: 14, fontWeight: 600, margin: '16px 0 8px'}}>Bạn phủ mỏng hơn hẳn ({gapResult.weaker.length})</h4>
							<Table rowKey="term" size="small"
								columns={termCols(gapResult.rival.url, 'weaker', [{
									title: 'Density của bạn', dataIndex: 'base_density', key: 'bd', width: 130, align: 'center',
									render: (v) => <span className={cn('hint')}>{v}%</span>,
								}])}
								dataSource={gapResult.weaker}
								scroll={{x: 'max-content'}}
								pagination={{pageSize: 10, hideOnSinglePage: true, showSizeChanger: false}} />
						</>
					)}
				</div>
			)}
		</div>
	);
}

export default KeywordGap;
